import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Star, X } from "lucide-react";
import { fetchDeclared, fetchSubjects, saveDeclared } from "@/pages/beta/courseFitApi";
import { CoursePool } from "@/pages/beta/FastMatchNext";

const btn = "inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] focus-visible:ring-offset-2";
const kicker = "text-[11px] uppercase tracking-[0.14em] text-[#7C3AED]";
const slugOf = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const MODES = [
  ["primary", "I have a main interest"],
  ["undecided", "I'm undecided between a few"],
  ["none", "No subject in mind yet"],
];

export function roleLabel(mode, i, n) {
  if (mode === "primary") return i === 0 ? "Main interest" : `Alternative ${i}`;
  return `Undecided · interest ${i + 1} of ${n}`;
}

function SubjectSearch({ subjects, chosen, onAdd, disabled }) {
  const [q, setQ] = useState("");
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return subjects.filter((s) => !chosen.includes(s.name) && s.name.toLowerCase().includes(t)).slice(0, 8);
  }, [q, subjects, chosen]);
  return (
    <div className="mt-4 max-w-xl">
      <label htmlFor="fm-interest-search" className="text-sm font-medium text-slate-800">Search for a subject</label>
      <div className="relative mt-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <input id="fm-interest-search" value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} autoComplete="off"
          placeholder={disabled ? "You've chosen three" : "Type at least 2 letters, e.g. Law"} data-testid="fm-interest-search"
          className="min-h-[44px] w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm focus:border-[#7C3AED] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 disabled:bg-slate-50" />
      </div>
      {hits.length > 0 && (
        <ul className="mt-1 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="fm-interest-results">
          {hits.map((s) => (
            <li key={s.name}>
              <button type="button" onClick={() => { onAdd(s.name); setQ(""); }} data-testid={`fm-interest-add-${slugOf(s.name)}`}
                className="flex min-h-[44px] w-full flex-col items-start px-3 py-2 text-left hover:bg-[#F5F0FF] focus:bg-[#F5F0FF] focus:outline-none">
                <span className="text-sm text-slate-900">{s.name}</span>
                <span className="text-[11px] text-slate-500">{s.family}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {q.trim().length >= 2 && !hits.length && <p className="mt-1 text-xs text-slate-500" data-testid="fm-interest-noresults">No matching subject in our list.</p>}
    </div>
  );
}

function ChosenList({ mode, chosen, onRemove, onMakeMain }) {
  if (!chosen.length) return null;
  return (
    <ol className="mt-4 max-w-xl space-y-2" data-testid="fm-interest-chosen">
      {chosen.map((n, i) => (
        <li key={n} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`fm-interest-chosen-${slugOf(n)}`}>
          <span className="text-[11px] uppercase tracking-[0.12em] text-[#7C3AED]" data-testid={`fm-interest-role-${slugOf(n)}`}>{roleLabel(mode, i, chosen.length)}</span>
          <span className="flex-1 text-sm font-medium text-slate-900">{n}</span>
          {mode === "primary" && i > 0 && (
            <button type="button" onClick={() => onMakeMain(n)} className="inline-flex min-h-[36px] items-center gap-1 rounded-full px-2 text-xs text-slate-700 hover:bg-slate-100" data-testid={`fm-interest-make-main-${slugOf(n)}`}>
              <Star className="h-3.5 w-3.5" aria-hidden /> Make main
            </button>
          )}
          <button type="button" onClick={() => onRemove(n)} aria-label={`Remove ${n}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100" data-testid={`fm-interest-remove-${slugOf(n)}`}>
            <X className="h-4 w-4" aria-hidden />
          </button>
        </li>
      ))}
    </ol>
  );
}

// Optional step between "Start Fast Match" and Q01. Saved apart from the answers; never scored.
export function InterestsStep({ session, prefillFrom, onDone, headingRef }) {
  const [subjects, setSubjects] = useState([]);
  const [mode, setMode] = useState("primary");
  const [chosen, setChosen] = useState([]);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let live = true;
    fetchSubjects().then((r) => live && r.ok && setSubjects(r.data.subjects));
    (async () => {
      let r = await fetchDeclared(session.sessionId, session.accessToken);
      if (!(r.ok && r.data.declared) && prefillFrom) r = await fetchDeclared(prefillFrom, null);
      const d = r.ok && r.data.declared;
      if (live && d) { setMode(d.mode === "undecided" && !d.interests.length ? "none" : d.mode); setChosen(d.interests); }
    })();
    return () => { live = false; };
  }, [session.sessionId, session.accessToken, prefillFrom]);
  const add = (n) => { setChosen((c) => (c.length < 3 ? [...c, n] : c)); setStatus(null); };
  const remove = (n) => { setChosen((c) => c.filter((x) => x !== n)); setStatus(null); };
  const makeMain = (n) => setChosen((c) => [n, ...c.filter((x) => x !== n)]);
  const canSave = mode === "none" || chosen.length > 0;
  const save = async () => {
    setStatus("saving");
    const body = mode === "none" ? { mode: "undecided", interests: [] } : { mode, interests: chosen };
    const r = await saveDeclared(session.sessionId, session.accessToken, body);
    if (!r.ok) { setStatus("error"); return; }
    onDone();
  };
  return (
    <div data-testid="fm-interests-step">
      <div className={`${kicker} mt-4`}>Before the questions · optional</div>
      <h1 ref={headingRef} tabIndex={-1} className="mt-1 text-3xl font-editorial tracking-tight text-slate-900 sm:text-4xl focus:outline-none" data-testid="fm-interests-heading">
        Do you already have a subject in mind?
      </h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-700 sm:text-base">
        Name up to three: a main interest and up to two alternatives, or a few you're undecided between.
        We keep these separately from your answers. They don't change how Fast Match scores you, and we'll show courses for each one next to your results.
      </p>
      <fieldset className="mt-5 flex flex-wrap gap-2" data-testid="fm-interests-mode">
        <legend className="sr-only">Which describes you?</legend>
        {MODES.map(([v, l]) => (
          <label key={v} className={`${btn} cursor-pointer ${mode === v ? "border-[#7C3AED] bg-[#F5F0FF] text-[#4C1D95]" : "border-slate-300 text-slate-700"}`}>
            <input type="radio" name="fm-interests-mode" value={v} checked={mode === v} onChange={() => { setMode(v); setStatus(null); }} className="sr-only" data-testid={`fm-interests-mode-${v}`} />
            {l}
          </label>
        ))}
      </fieldset>
      {mode !== "none" && (
        <>
          <SubjectSearch subjects={subjects} chosen={chosen} onAdd={add} disabled={chosen.length >= 3} />
          <ChosenList mode={mode} chosen={chosen} onRemove={remove} onMakeMain={makeMain} />
        </>
      )}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={!canSave || status === "saving"} data-testid="fm-interests-save"
          className={`${btn} border-slate-900 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50`}>
          {status === "saving" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Save and start the questions
        </button>
        <button type="button" onClick={onDone} className={`${btn} border-slate-300 text-slate-800 hover:bg-slate-50`} data-testid="fm-interests-skip">
          Skip this step
        </button>
        <span role="alert" className="text-sm text-rose-700" data-testid="fm-interests-error">{status === "error" ? "Couldn't save. Try again, or skip." : ""}</span>
      </div>
    </div>
  );
}

function declaredNote(subject, top5) {
  const row = top5.find((r) => r.canonicalSubject === subject);
  return row ? `You named this before the questions · also Fast Match #${row.rank}` : "You named this before the questions · not in your Fast Match top 5, and not scored";
}

// Results page: one labelled pool per interest named before the interview.
export function DeclaredInterestPools({ session, top5, anchors }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!session?.sessionId) return undefined;
    let live = true;
    fetchDeclared(session.sessionId, session.accessToken).then((r) => live && setD(r.ok ? r.data.declared : null));
    return () => { live = false; };
  }, [session?.sessionId, session?.accessToken]);
  if (!d?.interests?.length) return null;
  return (
    <section className="mt-10 print:hidden" data-testid="fm-declared-pools">
      <div className={kicker}>What you told us before the questions</div>
      <h2 className="mt-1 font-serif text-2xl text-stone-900">Courses for the subjects you named</h2>
      <p className="mt-2 max-w-2xl text-sm text-stone-600" data-testid="fm-declared-basis">
        {d.mode === "primary" ? "You chose a main interest and alternatives." : "You said you're undecided, so each is listed equally."}{" "}
        Saved separately from your answers. Fast Match's scores above don't use them.
      </p>
      <div className="mt-6 grid gap-4">
        {d.interests.map((n, i) => (
          <CoursePool key={n} subject={n} top5={top5} anchors={anchors} role={roleLabel(d.mode, i, d.interests.length)} note={declaredNote(n, top5)} testPrefix="fm-declared-pool" />
        ))}
      </div>
    </section>
  );
}
